import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { EVENT_TYPE_EMOJI } from "@/lib/games";
import { Plus, Zap, LogOut, Users, Gamepad2, ExternalLink, Loader2, Shield } from "lucide-react";

export default function Dashboard() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [events, setEvents] = useState(null);

  useEffect(() => { api.get("/events").then((r) => setEvents(r.data)).catch(() => setEvents([])); }, []);

  return (
    <div className="min-h-screen">
      <header className="glass border-b border-border sticky top-0 z-40">
        <div className="max-w-6xl mx-auto px-5 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2 font-head font-black text-xl cursor-pointer" onClick={() => navigate("/")}>
            <div className="w-8 h-8 rounded-lg bg-primary grid place-items-center brut-hard"><Zap className="w-5 h-5 text-white" /></div>Arenafy
          </div>
          <div className="flex items-center gap-2">
            {user?.role === "admin" && <Button variant="ghost" size="sm" className="rounded-full" onClick={() => navigate("/admin")} data-testid="admin-link"><Shield className="w-4 h-4 mr-1" />Platform Admin</Button>}
            <span className="hidden sm:flex items-center gap-2 text-sm text-muted-foreground">{user?.picture && <img src={user.picture} className="w-7 h-7 rounded-full" alt="" />}{user?.name}</span>
            <Button variant="ghost" size="icon" className="rounded-full" onClick={logout} data-testid="logout-btn"><LogOut className="w-4 h-4" /></Button>
          </div>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-5 py-10">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="font-head font-black text-3xl">Your Arenas</h1>
            <p className="text-muted-foreground">Create and manage gamified events.</p>
          </div>
          <Button className="rounded-full font-bold brut-hard" onClick={() => navigate("/dashboard/create")} data-testid="create-arena-btn"><Plus className="w-4 h-4 mr-1" />New Arena</Button>
        </div>

        {events === null && <div className="grid place-items-center py-20"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>}

        {events && events.length === 0 && (
          <div className="brut p-12 text-center">
            <div className="text-5xl mb-4">🎯</div>
            <h2 className="font-head font-black text-2xl mb-2">Create your first Arena</h2>
            <p className="text-muted-foreground mb-6">Turn any event into a game in under 5 minutes.</p>
            <Button className="rounded-full font-bold" onClick={() => navigate("/dashboard/create")}><Plus className="w-4 h-4 mr-1" />Create Your Arena</Button>
          </div>
        )}

        {events && events.length > 0 && (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {events.map((ev) => (
              <div key={ev.event_id} className="brut lift p-6 cursor-pointer hover:border-primary" onClick={() => navigate(`/arena/${ev.slug}`)} data-testid={`event-card-${ev.slug}`}>
                <div className="flex items-center justify-between mb-4">
                  <span className="text-3xl">{EVENT_TYPE_EMOJI[ev.event_type] || "✨"}</span>
                  <span className="text-xs px-2 py-1 rounded-full bg-neon-green/15 text-neon-green font-semibold uppercase">{ev.status}</span>
                </div>
                <h3 className="font-head font-black text-xl mb-1">{ev.name}</h3>
                <p className="text-sm text-muted-foreground mb-4">{ev.event_type} · {ev.date || "No date"}</p>
                <div className="flex gap-4 text-sm">
                  <span className="flex items-center gap-1 text-muted-foreground"><Users className="w-4 h-4" /><span className="font-mono-num">{ev.player_count}</span></span>
                  <span className="flex items-center gap-1 text-muted-foreground"><Gamepad2 className="w-4 h-4" /><span className="font-mono-num">{ev.zone_count}</span></span>
                  <span className="ml-auto flex items-center gap-1 text-primary font-semibold">Manage <ExternalLink className="w-3.5 h-3.5" /></span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
